import React, { useEffect, useState } from "react";
import api from "../../utils/axios";
import { AgGridReact } from "ag-grid-react";
import { Button, Divider, Modal } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import DefaultInput from "../../components/form/DefaultInput";
import { toast } from "react-toastify";
import useLang from "../../hooks/useLang";
import { cashRegistersLookup } from "../../lib/cachedLookups";
import useLookup from "../../hooks/useLookup";
import DefaultSelect from "../../components/form/DefaultSelect";
import classes from "./MovingBetweenCashRegister.module.css";
import { useMediaQuery } from "@mui/material";

type Currency = {
	id: number;
	description: string;
	rate: number;
};
const CashRegisterManager = () => {
	const { strings } = useLang("cashRegisterManagement");
	const defaultColDef = {
		sortable: true,
		filter: true,
		wrapHeaderText: true,
		resizable: true,
		filterParams: { excelMode: "windows" },
		autoSizeAllColumns: true,
	};
	const [currencies, setCurrencies] = useState<Currency[]>([]);
	const [currencyId, setCurrencyId] = useState<number | null>(null);
	const [currencyDropdown, setCurrencyDropdown] = useState<any>(null);

	const isSmallScreen = useMediaQuery("(max-width: 994px)");

	const tableColumns = [
		{
			field: "id",
			headerName: "Id",
			type: "numericColumn",
			filter: "agNumberColumnFilter",
			suppressSizeToFit: isSmallScreen,
		},
		{
			field: "description",
			headerName: strings?.description,
			editable: true,
			suppressSizeToFit: isSmallScreen,
		},
		{
			field: "currencyId",
			headerName: "Currency id",
			editable: true,
			suppressSizeToFit: isSmallScreen,
		},
		{
			field: "currencyDescription",
			headerName: strings?.currency,
			editable: false,
			suppressSizeToFit: isSmallScreen,
		},
	];
	const [openAddRegisterModal, setOpenAddRegisterModal] =
		useState<boolean>(false);
	const createRegister = async (description: string, currencyId: any) => {
		try {
			const response = await api.post("CashRegister/insert", {
				id: -1,
				description,
				currencyId: currencyId,
				currencyDescription: "",
			});
			if (response.status === 200) {
				fetchCashRegisters();
				setOpenAddRegisterModal(false);
				setDescription("");
			}
		} catch (error) {
			console.error("Error creating cash register", error);
		}

		return null;
	};

	const fetchCurrencies = async () => {
		try {
			const response = await api.get<Currency[]>("Currency");
			if (response.status === 200) {
				const data = response.data;
				setCurrencies(data);
				const formattedCurrencies = data?.map((item: any) => ({
					value: item.id,
					label: item.description,
				}));
				setCurrencyDropdown(formattedCurrencies);
			}
		} catch (error) {
			console.error("Error fetching Currencies:", error);
		}

		return [];
	};
	const updateRegister = async (
		id: number,
		description: string,
		currencyId: number
	) => {
		try {
			const response = await api.post("CashRegister/Update", {
				id,
				description,
				currencyId: currencyId,
				currencyDescription: "",
			});
			if (response.status === 204) {
				console.log("Cash register updated");
				fetchCashRegisters();
			}
		} catch (error) {
			console.error("Error updating cash register:", error);
		}
	};
	// This screen edits the cash registers: `fetchCashRegisters` fetches them fresh and updates the
	// copy every other screen reads.
	const { data: cashRegisters = [], reload: fetchCashRegisters } =
		useLookup(cashRegistersLookup);
	const [description, setDescription] = useState<string>("");

	useEffect(() => {
		fetchCurrencies();
	}, []);

	const onRowEdit = async (row: any) => {
		await updateRegister(
			row.data.id,
			row.data.description,
			row.data.currencyId
		);
		await fetchCashRegisters();
	};

	const handleAddRegister = async () => {
		if (description.length <= 0) {
			toast.error(strings?.descriptionEmpty);
			return;
		}
		if (!currencyId) {
			toast.error(strings?.selectCurrency);
		}
		await createRegister(description, currencyId);
		await fetchCashRegisters();
	};

	return (
		<>
			<Modal
				open={openAddRegisterModal}
				title={strings?.addCashRegister}
				onCancel={() => setOpenAddRegisterModal(false)}
				onOk={handleAddRegister}
				okButtonProps={{ className: "bg-blue-500" }}
			>
				<Divider />
				<div className={classes.inputContainer}>
					<DefaultInput
						placeholder={strings?.description}
						className={classes.secondInput}
						value={description}
						label={strings?.cashRegister}
						onChange={(e) => setDescription(e.target.value)}
					/>
					<DefaultSelect
						label={strings?.currency}
						className={classes.secondInput}
						placeholder={strings?.currency}
						options={currencyDropdown}
						onChange={(value) => setCurrencyId(value)}
						allowClear={true}
						onClear={() => setCurrencyId(null)}
						alignCenter="start"
						flexDirection="col"
					/>
				</div>
			</Modal>
			<div className={classes.mainContainer}>
				<div className={classes.heroContainer}>
					<Button
						className={classes.button}
						onClick={() => setOpenAddRegisterModal(true)}
					>
						<PlusOutlined />
						{strings?.add}
					</Button>
					<div
						className="ag-theme-alpine"
						style={{
							height: 600,
						}}
					>
						<AgGridReact
							rowData={cashRegisters}
							defaultColDef={defaultColDef}
							columnDefs={tableColumns}
							enterMovesDown={true}
							enterMovesDownAfterEdit={true}
							undoRedoCellEditing={true}
							undoRedoCellEditingLimit={20}
							suppressCopyRowsToClipboard={true}
							rowSelection="single"
							enableRangeSelection={true}
							editType={"fullRow"}
							onRowValueChanged={(event) => {
								onRowEdit(event);
							}}
						/>
					</div>
				</div>
			</div>
		</>
	);
};

export default CashRegisterManager;
